import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, Phone, Mail, Calendar, Clock, DollarSign, MapPin, User, 
  ExternalLink, Wrench, Users, CheckSquare, Truck, FileCheck, Receipt,
  CreditCard, CheckCircle2, RefreshCw, Wifi, WifiOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { filterJobsByPermissions } from '../../utils/permissions';
import { useBoardWebSocket } from '../../hooks/useBoardWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Operations/Production workflow stages (aligned with Builder Prime)
const OPS_STAGES = [
  { id: 'deposit_received', name: 'Deposit Received', color: 'bg-cyan-500', icon: CreditCard, description: 'Deposit received' },
  { id: 'contacted', name: 'Contacted', color: 'bg-teal-500', icon: Phone, description: 'Customer contacted' },
  { id: 'scheduled', name: 'Scheduled', color: 'bg-blue-500', icon: Calendar, description: 'Install date set' },
  { id: 'in_progress', name: 'In Progress', color: 'bg-orange-500', icon: Truck, description: 'Work in progress' },
  { id: 'collections', name: 'Collections', color: 'bg-yellow-500', icon: Receipt, description: 'Awaiting payment' },
  { id: 'complete', name: 'Complete', color: 'bg-green-600', icon: CheckCircle2, description: 'Complete!' },
  { id: 'cancelled', name: 'Cancelled', color: 'bg-red-500', icon: RefreshCw, description: 'Cancelled jobs' },
  { id: 'sale_lost', name: 'Sale Lost', color: 'bg-gray-500', icon: RefreshCw, description: 'Lost sales' },
];

// BP Status to stage ID mapping (case-insensitive handled in mapToOpsStage)
const BP_STATUS_TO_STAGE = {
  'Sold': 'deposit_received',
  'sold': 'deposit_received',
  'Deposit Received': 'deposit_received',
  'Sale Contingent': 'deposit_received',
  'Contacted': 'contacted',
  'Scheduled': 'scheduled',
  'scheduled': 'scheduled',
  'In Progress': 'in_progress',
  'active': 'in_progress',
  'Collections (Complete, Need Payment)': 'collections',
  'Complete': 'complete',
  'Recoating': 'complete',
  'Cancelled': 'cancelled',
  'Sale Lost': 'sale_lost',
  'None': 'deposit_received',
  'appointment_set': 'scheduled',
};

// Ops-relevant BP statuses (all statuses that should appear on Ops board)
const OPS_BP_STATUSES = [
  'Sold', 'sold', 'Deposit Received', 'Sale Contingent', 'Contacted', 
  'Scheduled', 'scheduled', 'In Progress', 'active',
  'Collections (Complete, Need Payment)', 'Complete', 'Recoating',
  'Cancelled', 'Sale Lost', 'None', 'appointment_set'
];

// Map incoming job to ops stage using BP status
const mapToOpsStage = (job) => {
  const bpStatus = job.bp_status_name || job.status || '';
  
  // Use BP status mapping first
  if (bpStatus && BP_STATUS_TO_STAGE[bpStatus]) {
    return BP_STATUS_TO_STAGE[bpStatus];
  }
  
  const stage = job.stage || job.status || 'deposit_received';
  
  // Direct mapping for ops stages
  if (OPS_STAGES.some(s => s.id === stage)) {
    return stage;
  }
  
  return 'deposit_received';
};

export const OperationsProductionBoard = () => {
  const navigate = useNavigate();
  const { user, viewAsRole, viewAsUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterCrew, setFilterCrew] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });

  // Real-time WebSocket for live updates
  const handleRealtimeUpdate = useCallback((data, updateType, board) => {
    console.log(`[OpsBoard] Real-time update: ${updateType}`, data?.id);
    fetchData();
  }, []);

  const { isConnected: wsConnected } = useBoardWebSocket('operations', {
    onProjectUpdate: handleRealtimeUpdate,
    onAssignmentChange: handleRealtimeUpdate,
    onBulkUpdate: () => fetchData(),
    enabled: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, usersRes, crewsRes] = await Promise.all([
        axios.get(`${API}/jobs?limit=5000`),
        axios.get(`${API}/users`),
        axios.get(`${API}/crews`).catch(() => ({ data: [] }))
      ]);
      
      const jobs = jobsRes.data || [];
      console.log(`Production Board: Fetched ${jobs.length} total jobs from API`);
      
      // Filter to only show ops-relevant BP statuses and map to stage IDs
      const opsProjects = jobs
        .filter(job => {
          const bpStatus = job.bp_status_name || job.status || '';
          return OPS_BP_STATUSES.includes(bpStatus);
        })
        .map(job => ({
          ...job,
          stage: mapToOpsStage(job),
          bp_status_name: job.bp_status_name || job.status || ''
        }));
      
      console.log(`Operations Production Board: ${opsProjects.length} jobs from ${jobs.length} total`);
      setAllProjects(opsProjects);
      setUsers(usersRes.data || []);
      setCrews(crewsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load board data');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleBasedFilter = useCallback(() => {
    if (!allProjects || allProjects.length === 0) {
      setProjects([]);
      return;
    }

    let effectiveUser = user;
    if (viewAsUser && viewAsRole) {
      const testUser = users.find(u => u.id === viewAsUser);
      if (testUser) {
        effectiveUser = testUser;
      }
    }

    const filteredProjects = filterJobsByPermissions(allProjects, effectiveUser, users);
    setProjects(filteredProjects);
  }, [allProjects, user, viewAsUser, viewAsRole, users]);

  useEffect(() => {
    applyRoleBasedFilter();
  }, [applyRoleBasedFilter]);

  // Navigate to workspace when card is clicked
  const handleCardClick = (project) => {
    navigate(`/workspace/records?source=ops&mode=projects&id=${project.id}`);
  };

  const handleDragStart = (e, project) => {
    setDraggedCard(project);
    setIsDragging(true);
    setDragPosition({ x: e.clientX, y: e.clientY });
    e.dataTransfer.effectAllowed = 'move';
    
    const transparentImg = document.createElement('img');
    transparentImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(transparentImg, 0, 0);
    
    const handleMouseMove = (evt) => {
      evt.preventDefault();
      setDragPosition({ x: evt.clientX, y: evt.clientY });
    };
    
    document.addEventListener('dragover', handleMouseMove);
    window._dragCleanup = () => {
      document.removeEventListener('dragover', handleMouseMove);
    };
  };

  const handleDragEnd = () => {
    if (window._dragCleanup) {
      window._dragCleanup();
      window._dragCleanup = null;
    }
    setIsDragging(false);
    setDraggedCard(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (stageId) => {
    setDragOverStage(stageId);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedCard) {
      handleDragEnd();
      return;
    }
    
    const currentOpsStage = mapToOpsStage(draggedCard);
    if (currentOpsStage === targetStage) {
      handleDragEnd();
      return;
    }

    const movedProject = draggedCard;
    handleDragEnd();

    // Optimistic update
    setProjects(prev => prev.map(p => 
      p.id === movedProject.id 
        ? { ...p, stage: targetStage, status: targetStage }
        : p
    ));
    setAllProjects(prev => prev.map(p => 
      p.id === movedProject.id 
        ? { ...p, stage: targetStage, status: targetStage }
        : p
    ));

    try {
      await axios.patch(`${API}/jobs/${movedProject.id}`, {
        stage: targetStage,
        status: targetStage
      });
      
      const stageName = OPS_STAGES.find(s => s.id === targetStage)?.name;
      toast.success(`Moved to ${stageName}`);
    } catch (error) {
      // Revert on error
      setProjects(prev => prev.map(p => 
        p.id === movedProject.id ? movedProject : p
      ));
      setAllProjects(prev => prev.map(p => 
        p.id === movedProject.id ? movedProject : p
      ));
      console.error('Update stage error:', error);
      toast.error('Failed to update stage');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  const getDaysInStage = (project) => {
    const dateField = project.stage_entered_at || project.updated_at || project.created_at;
    if (!dateField) return 0;
    const entered = new Date(dateField);
    const now = new Date();
    return Math.floor((now - entered) / (1000 * 60 * 60 * 24));
  };

  const filteredProjects = projects.filter(project => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      project.customer_name?.toLowerCase().includes(search) ||
      project.customer_email?.toLowerCase().includes(search) ||
      project.customer_phone?.includes(search) ||
      project.address?.toLowerCase().includes(search);
    
    const matchesAssignedTo = filterAssignedTo === 'all' || project.assigned_to === filterAssignedTo;
    const matchesCrew = filterCrew === 'all' || project.crew_id === filterCrew;
    const matchesPriority = filterPriority === 'all' || project.priority === filterPriority;
    
    return matchesSearch && matchesAssignedTo && matchesCrew && matchesPriority;
  });

  const getProjectsByStage = (stageId) => {
    return filteredProjects.filter(project => {
      const opsStage = mapToOpsStage(project);
      return opsStage === stageId;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading production board...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">Production Board</h1>
              {/* Real-time connection indicator */}
              <div 
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                  wsConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}
                title={wsConnected ? 'Real-time updates active' : 'Connecting...'}
              >
                {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                <span>{wsConnected ? 'Live' : 'Offline'}</span>
              </div>
            </div>
            <p className="text-muted-foreground">Manage operations and production workflow</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/workspace/records?source=ops&mode=projects')} 
              variant="outline"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Workspace
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-white/80">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                  <Input
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-9 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-white/80">Assigned To</Label>
                <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
                  <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white/80">Crew</Label>
                <Select value={filterCrew} onValueChange={setFilterCrew}>
                  <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Crews</SelectItem>
                    {crews.map(crew => (
                      <SelectItem key={crew.id} value={crew.id}>{crew.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white/80">Priority</Label>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Board */}
        <div className="overflow-x-auto pb-4">
          <div className="inline-flex gap-3 min-w-full">
            {OPS_STAGES.map((stage) => {
              const Icon = stage.icon;
              const stageProjects = getProjectsByStage(stage.id);
              const isPaidStage = stage.id === 'paid';
              
              return (
                <div 
                  key={stage.id}
                  className={`flex-shrink-0 w-64 ${isPaidStage ? 'opacity-70' : ''}`}
                  onDragOver={handleDragOver}
                  onDragEnter={() => handleDragEnter(stage.id)}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  <div className={`${stage.color} text-white rounded-t-lg p-3 transition-all ${
                    dragOverStage === stage.id ? 'ring-2 ring-white shadow-lg' : ''
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span className="font-semibold text-sm">{stage.name}</span>
                      </div>
                      <Badge variant="secondary" className="bg-white/20 text-white text-xs">
                        {stageProjects.length}
                      </Badge>
                    </div>
                    <p className="text-xs text-white/70 mt-1">{stage.description}</p>
                  </div>
                  
                  <div className={`bg-slate-100 dark:bg-slate-900 rounded-b-lg p-2 min-h-[400px] max-h-[550px] overflow-y-auto transition-all ${
                    dragOverStage === stage.id ? 'ring-2 ring-inset ring-blue-400' : ''
                  }`}>
                    {stageProjects.map(project => (
                      <div
                        key={project.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, project)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleCardClick(project)}
                        className={`bg-white dark:bg-slate-800 rounded-lg p-3 mb-2 shadow-sm hover:shadow-md cursor-pointer border transition-all ${
                          draggedCard?.id === project.id 
                            ? 'opacity-40 border-dashed border-blue-400' 
                            : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                        }`}
                      >
                        <div className="space-y-2">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-sm truncate">{project.customer_name}</h4>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getPriorityColor(project.priority)}`} 
                                   title={project.priority} />
                              <ExternalLink className="h-3 w-3 text-blue-500" />
                            </div>
                          </div>
                          
                          {/* Address */}
                          {project.address && (
                            <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-1">{project.address}</span>
                            </div>
                          )}
                          
                          {/* Value - prefer total_contracted (actual contract) over estimated_value */}
                          {(project.total_contracted || project.estimated_value) && (
                            <div className="flex items-center gap-1.5 text-xs">
                              <DollarSign className="h-3 w-3 text-emerald-600" />
                              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                                ${(project.total_contracted || project.estimated_value).toLocaleString()}
                              </span>
                              {!project.total_contracted && project.estimated_value && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400">(est)</span>
                              )}
                            </div>
                          )}
                          
                          {/* Install date */}
                          {project.install_date && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>Install: {new Date(project.install_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          
                          {/* Crew */}
                          {project.crew_name && (
                            <div className="flex items-center gap-1.5 text-xs">
                              <Users className="h-3 w-3 text-indigo-600" />
                              <span className="text-indigo-700 dark:text-indigo-400">{project.crew_name}</span>
                            </div>
                          )}
                          
                          {/* Footer */}
                          <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>{getDaysInStage(project)}d</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {project.customer_phone && (
                                <button 
                                  className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `tel:${project.customer_phone}`;
                                  }}
                                >
                                  <Phone className="h-3 w-3 text-slate-500" />
                                </button>
                              )}
                              {project.customer_email && (
                                <button 
                                  className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `mailto:${project.customer_email}`;
                                  }}
                                >
                                  <Mail className="h-3 w-3 text-slate-500" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {stageProjects.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        No projects
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Floating drag preview */}
        {isDragging && draggedCard && (
          <div 
            className="fixed pointer-events-none z-50 bg-white dark:bg-slate-800 rounded-lg p-3 shadow-2xl border-2 border-amber-500 w-56 opacity-90"
            style={{
              left: dragPosition.x + 10,
              top: dragPosition.y + 10,
            }}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getPriorityColor(draggedCard.priority)}`} />
              <span className="font-semibold text-sm truncate">{draggedCard.customer_name}</span>
            </div>
            {draggedCard.address && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{draggedCard.address}</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OperationsProductionBoard;
